/* eslint-disable import/prefer-default-export */
import {
  Hubs,
  HubSectionType,
  HubSplitSectionContentType,
  HubSplitSectionImageContent,
  HubUpsertParams,
} from 'utils/gloo-types';
import { omit } from 'lodash';
import { v1 } from 'uuid';
import { Store } from './reducer';
import {
  ClientHubSection,
  ClientHubUpsertParams,
  HubAuthoringFormErrors,
  HubAuthoringFormProperty,
  HubAuthoringSectionProperty,
} from './types';

/**
 * Replaces optional empty strings in the hubUpsertParams object with null.
 */
export function sanitizeHubUpsertParams(hubUpsertParams: ClientHubUpsertParams): HubUpsertParams {
  return {
    ...(omit(hubUpsertParams, ['featuredPriority', 'archivedAt']) as HubUpsertParams),
    sections: hubUpsertParams.sections.map((hubSection: ClientHubSection) => {
      let modifiedHubSection: ClientHubSection;

      switch (hubSection.type) {
        case HubSectionType.IMAGE: {
          modifiedHubSection = {
            ...hubSection,
            imageUrl: hubSection.imageUrl === '' ? null : hubSection.imageUrl,
            linkUrl: hubSection.linkUrl === '' ? null : hubSection.linkUrl,
          };
          break;
        }
        case HubSectionType.SPLIT: {
          modifiedHubSection = { ...hubSection };

          if (hubSection.a.type === HubSplitSectionContentType.IMAGE && hubSection.a.linkUrl === '') {
            (modifiedHubSection.a as HubSplitSectionImageContent).linkUrl = null;
          }

          if (hubSection.b.type === HubSplitSectionContentType.IMAGE && hubSection.b.linkUrl === '') {
            (modifiedHubSection.b as HubSplitSectionImageContent).linkUrl = null;
          }

          break;
        }
        default:
          modifiedHubSection = { ...hubSection };
      }

      delete modifiedHubSection.clientId;

      return modifiedHubSection;
    }),
  };
}

/**
 * Replace null with an empty string where appropriate and add clientIds to sections.
 */
export function extractSanitizedHubUpsertParamsFrom(hub: Hubs): ClientHubUpsertParams {
  const {
    author,
    title,
    shortDescription,
    longDescription,
    backgroundColor,
    titleColor,
    descriptionColor,
    logoImageUrl,
    thumbnailImageUrl,
    headerImageUrl,
    tags,
    visibleInDiscover,
    orgTypeFiltering,
    sections,
  } = hub;

  return {
    author,
    title,
    shortDescription,
    longDescription,
    backgroundColor,
    titleColor,
    descriptionColor,
    logoImageUrl,
    thumbnailImageUrl,
    headerImageUrl,
    tags,
    visibleInDiscover,
    orgTypeFiltering,
    sections: sections.map((hubSection: ClientHubSection): ClientHubSection => {
      let section: ClientHubSection;

      switch (hubSection.type) {
        case HubSectionType.IMAGE: {
          section = {
            ...hubSection,
            imageUrl: hubSection.imageUrl === null ? '' : hubSection.imageUrl,
            linkUrl: hubSection.linkUrl === null ? '' : hubSection.linkUrl,
          };
          break;
        }
        case HubSectionType.SPLIT: {
          section = { ...hubSection };

          if (hubSection.a.type === HubSplitSectionContentType.IMAGE && hubSection.a.linkUrl === null) {
            (section.a as HubSplitSectionImageContent).linkUrl = '';
          }

          if (hubSection.b.type === HubSplitSectionContentType.IMAGE && hubSection.b.linkUrl === null) {
            (section.b as HubSplitSectionImageContent).linkUrl = '';
          }

          break;
        }
        case HubSectionType.RESOURCE: {
          section = {
            ...omit(hubSection, ['resources']),
          };
          break;
        }
        default:
          section = hubSection;
      }

      section.clientId = v1();

      return section;
    }),
  };
}

export function getFormErrors(state: Store): HubAuthoringFormErrors[] {
  const errors: HubAuthoringFormErrors[] = [];
  const formProperties = Object.keys(HubAuthoringFormProperty);
  const sectionProperties = Object.keys(HubAuthoringSectionProperty);

  formProperties.forEach((key: HubAuthoringFormProperty) => {
    const value = state.hubUpsertParams[key];

    if (typeof value === 'string' && value.length > 0) {
      return;
    }

    // Handle errors for sections
    if (key === HubAuthoringFormProperty.sections) {
      // Loop through each section and validate property values
      state.hubUpsertParams[HubAuthoringFormProperty.sections].forEach((section: ClientHubSection) => {
        sectionProperties.forEach((sectionKey: HubAuthoringSectionProperty) => {
          const sectionId = section.clientId;

          let error: HubAuthoringFormErrors;

          switch (section.type) {
            case HubSectionType.IMAGE: {
              if (typeof section.imageUrl !== 'string' || section.imageUrl.length < 1) {
                error = {
                  [HubAuthoringFormProperty.sections]: {
                    [sectionId]: {
                      [sectionKey]: 'An image or video is required.',
                    },
                  },
                };

                errors.push(error);
              }
              break;
            }
            case HubSectionType.SPLIT: {
              switch (sectionKey) {
                case HubAuthoringSectionProperty.imageUrl: {
                  if (
                    HubAuthoringSectionProperty.imageUrl in section.a &&
                    (typeof section.a.imageUrl !== 'string' || section.a.imageUrl.length < 1)
                  ) {
                    error = {
                      [HubAuthoringFormProperty.sections]: {
                        [sectionId]: {
                          a: {
                            [sectionKey]: 'An image or video is required.',
                          },
                        },
                      },
                    };

                    errors.push(error);
                  }

                  if (
                    HubAuthoringSectionProperty.imageUrl in section.b &&
                    (typeof section.b.imageUrl !== 'string' || section.b.imageUrl.length < 1)
                  ) {
                    error = {
                      [HubAuthoringFormProperty.sections]: {
                        [sectionId]: {
                          b: {
                            [sectionKey]: 'An image or video is required.',
                          },
                        },
                      },
                    };

                    errors.push(error);
                  }
                  break;
                }
              }
              break;
            }
          }
        });
      });
      return;
    }

    // Get errors if a required field is missing
    let error: HubAuthoringFormErrors;

    switch (key) {
      case HubAuthoringFormProperty.title: {
        error = {
          [key]: `A title is required.`,
        } as HubAuthoringFormErrors;
        break;
      }
      case HubAuthoringFormProperty.author: {
        error = {
          [key]: `An author is required.`,
        } as HubAuthoringFormErrors;
        break;
      }
      case HubAuthoringFormProperty.logoImageUrl: {
        error = {
          [key]: `A logo image URL is required.`,
        } as HubAuthoringFormErrors;
        break;
      }
      case HubAuthoringFormProperty.headerImageUrl: {
        error = {
          [key]: `A header image URL is required.`,
        } as HubAuthoringFormErrors;
        break;
      }
      case HubAuthoringFormProperty.thumbnailImageUrl: {
        error = {
          [key]: `A thumbnail image URL is required.`,
        } as HubAuthoringFormErrors;
        break;
      }
      default: {
        error = {
          [key]: `This field is required.`,
        } as HubAuthoringFormErrors;
      }
    }

    errors.push(error);
  });

  return errors;
}
